import { useState } from 'react';
import { Check, X, Merge } from 'lucide-react';
import { useTranslation } from '../../i18n';
import styles from './DuplicateReviewPanel.module.css';

interface ItemSummary {
  id: string;
  name: string;
  category: string;
  platform: string;
  condition: string;
  imageUrl: string | null;
  createdAt: string;
}

interface Props {
  item1: ItemSummary;
  item2: ItemSummary;
  similarity: number;
  onDismiss: (item1Id: string, item2Id: string) => void;
  onMerge: (keepItemId: string, removeItemId: string) => void;
  isPending: boolean;
}

export function DuplicateReviewPanel({ item1, item2, similarity, onDismiss, onMerge, isPending }: Props) {
  const { t } = useTranslation();
  const [confirmMerge, setConfirmMerge] = useState<'left' | 'right' | null>(null);

  function handleMerge(keepSide: 'left' | 'right') {
    if (confirmMerge === keepSide) {
      const keepId = keepSide === 'left' ? item1.id : item2.id;
      const removeId = keepSide === 'left' ? item2.id : item1.id;
      onMerge(keepId, removeId);
      setConfirmMerge(null);
    } else {
      setConfirmMerge(keepSide);
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.simBadge}>
        {Math.round(similarity * 100)}% {t('duplicates.match')}
      </div>

      <div className={styles.comparison}>
        <ItemColumn item={item1} />
        <div className={styles.divider}>
          <Merge size={16} strokeWidth={1.75} />
        </div>
        <ItemColumn item={item2} />
      </div>

      <div className={styles.actions}>
        <button
          className={styles.dismissBtn}
          onClick={() => onDismiss(item1.id, item2.id)}
          disabled={isPending}
        >
          <X size={14} strokeWidth={2} />
          {t('duplicates.notDuplicate')}
        </button>
        <button
          className={`${styles.mergeBtn} ${confirmMerge === 'left' ? styles.mergeBtnConfirm : ''}`}
          onClick={() => handleMerge('left')}
          disabled={isPending}
        >
          <Check size={14} strokeWidth={2} />
          {confirmMerge === 'left' ? t('duplicates.confirmKeep') : t('duplicates.keepLeft')}
        </button>
        <button
          className={`${styles.mergeBtn} ${confirmMerge === 'right' ? styles.mergeBtnConfirm : ''}`}
          onClick={() => handleMerge('right')}
          disabled={isPending}
        >
          <Check size={14} strokeWidth={2} />
          {confirmMerge === 'right' ? t('duplicates.confirmKeep') : t('duplicates.keepRight')}
        </button>
      </div>
    </div>
  );
}

function ItemColumn({ item }: { item: ItemSummary }) {
  const { t } = useTranslation();

  return (
    <div className={styles.itemCol}>
      {item.imageUrl ? (
        <img src={item.imageUrl} alt={item.name} className={styles.itemImg} />
      ) : (
        <div className={styles.itemImgPlaceholder} />
      )}
      <div className={styles.itemName}>{item.name}</div>
      <div className={styles.itemMeta}>
        <span>{item.platform}</span>
        <span>{item.category}</span>
        <span>{t(`condition.${item.condition}` as any) || item.condition}</span>
      </div>
    </div>
  );
}
